const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const amount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const BILLABLE_CASE_STATUSES = new Set([
  "Completata",
  "Conclusa",
  "Chiusa",
  "Nuova richiesta con fattura",
  "In Lavorazione",
]);

export function isInvoiceLinkedToCase(invoice, caseId) {
  const id = String(caseId || "");
  if (!id || !invoice) return false;
  if (String(invoice.caseId || "") === id) return true;
  return (invoice.linkedPractices || []).some((linkedId) => String(linkedId) === id);
}

export function invoiceTaxableAmount(invoice) {
  if (!invoice) return 0;
  const rows = (invoice.manualRows || []).reduce(
    (sum, row) => sum + amount(row?.amount ?? row?.amountNet),
    0
  );
  return round2(amount(invoice.imponibile) + rows);
}

function isOfficialInvoice(invoice) {
  const type = invoice?.documentType || "invoice";
  return type === "invoice" && invoice?.status !== "Stornata";
}

function creditNotesForInvoice(invoice, invoices) {
  const invoiceId = String(invoice?.id || invoice?._id || "");
  if (!invoiceId) return [];
  return (invoices || []).filter((candidate) => {
    if (candidate?.documentType !== "credit_note") return false;
    if (candidate?.status === "Stornata") return false;
    return String(candidate?.sourceInvoiceId || "") === invoiceId;
  });
}

export function resolvePracticeFeeMode(caseItem, invoices = [], clients = []) {
  if (caseItem?.feeMode === "lordo" || caseItem?.feeMode === "netto") {
    return caseItem.feeMode;
  }

  const firstLinkedInvoice = (invoices || [])
    .filter((invoice) => isOfficialInvoice(invoice) && isInvoiceLinkedToCase(invoice, caseItem?.id))
    .sort((a, b) => String(a.invoiceDate || "").localeCompare(String(b.invoiceDate || "")))[0];

  if (firstLinkedInvoice && typeof firstLinkedInvoice.importoLordo === "boolean") {
    return firstLinkedInvoice.importoLordo ? "lordo" : "netto";
  }

  const client = (clients || []).find(
    (candidate) => String(candidate.id) === String(caseItem?.clientId)
  );
  const privateWithoutVat = client
    && (
      client.clientType === "Privato"
      || !client.vatNumber
      || String(client.vatNumber).trim() === ""
    );
  return privateWithoutVat ? "lordo" : "netto";
}

export function effectiveInvoiceAmount(invoice, mode, invoices = []) {
  if (!isOfficialInvoice(invoice)) return 0;

  const original = mode === "lordo"
    ? amount(invoice.totale)
    : invoiceTaxableAmount(invoice);

  const linkedCreditNotes = creditNotesForInvoice(invoice, invoices);
  let credited = 0;
  if (linkedCreditNotes.length > 0) {
    credited = linkedCreditNotes.reduce(
      (sum, note) => sum + (
        mode === "lordo" ? amount(note.totale) : invoiceTaxableAmount(note)
      ),
      0
    );
  } else if (mode === "lordo") {
    credited = amount(invoice.creditedAmount);
  }

  return round2(Math.max(0, original - credited));
}

export function getPracticeBilling(caseItem, invoices = [], clients = []) {
  const agreedAmount = round2(
    amount(caseItem?.finalFee) > 0 ? caseItem.finalFee : caseItem?.estimatedFee
  );
  const mode = resolvePracticeFeeMode(caseItem, invoices, clients);

  const installments = (invoices || [])
    .filter((invoice) => isOfficialInvoice(invoice) && isInvoiceLinkedToCase(invoice, caseItem?.id))
    .map((invoice) => ({
      invoiceId: String(invoice.id || invoice._id || ""),
      invoiceNumber: invoice.invoiceNumber || "",
      invoiceDate: invoice.invoiceDate || "",
      amount: effectiveInvoiceAmount(invoice, mode, invoices),
    }))
    .filter((installment) => installment.amount > 0)
    .sort((a, b) => String(a.invoiceDate).localeCompare(String(b.invoiceDate)));

  const invoicedAmount = round2(
    installments.reduce((sum, installment) => sum + installment.amount, 0)
  );
  const residualAmount = round2(Math.max(0, agreedAmount - invoicedAmount));
  const fullyInvoiced = agreedAmount > 0 && residualAmount <= 0.01;

  return {
    mode,
    agreedAmount,
    invoicedAmount,
    residualAmount,
    fullyInvoiced,
    partiallyInvoiced: invoicedAmount > 0 && !fullyInvoiced,
    installments,
  };
}

export function buildBalanceDeductionRows(billing) {
  return (billing?.installments || []).map((installment, index) => ({
    id: `advance-${installment.invoiceId || index}`,
    description: `Acconto fattura ${installment.invoiceNumber}${
      installment.invoiceDate ? ` del ${installment.invoiceDate.split("-").reverse().join("/")}` : ""
    }`,
    amount: -round2(installment.amount),
    amountInput: -round2(installment.amount),
    amountMode: billing.mode,
    isAdvanceDeduction: true,
    sourceInvoiceId: installment.invoiceId,
  }));
}
